import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage24() {
const contentRef = useRef(null);
   const links = {
    prev: "/islamic-trade-page-23", 
    index: "/index",                 
    next: "/islamic-trade-page-25", 
  };
  const titleText = "Rulings Pertaining to Endowments (Waqf)";
  const questions = [
    {
      text: "1. What is the primary characteristic of a Waqf property in Islamic law?",
      options: [
        {
          value: "A",
          label:
            "A. It can be sold or inherited.",
        },
        {
          value: "B",
          label:
            "B. It must generate profit for the founder.",
        },
        {
          value: "C",
          label:
            "C. It must be permanently dedicated for a charitable purpose.",
        },
        {
          value: "D",
          label:
            "D. It is returned to the government after 10 years.",
        },
      ],
      correct: "C",
    },
    {
      text: "2. Who is responsible for managing and maintaining the Waqf property?",
      options: [
        { value: "A", label: "A. The government" },
        { value: "B", label: "B. The donor’s heirs" },
        { value: "C", label: "C. The Mutawalli (trustee)" },
        { value: "D", label: "D. The local imam" },
      ],
      correct: "C",
    },
    {
      text: "3. Which of the following is a valid example of Waqf in Islamic tradition??",
      options: [
        { value: "A", label: "A. Investing money for a business ventures" },
        { value: "B", label: "B. Renting a book to a friend" },
        { value: "C", label: "C. Dedicating a piece of land for building a mosque" },
        { value: "D", label: "D. Giving a loan to a relative without interestg" },
      ],
      correct: "C",
    },
    {
      text: "4. What is the main purpose of establishing a Waqf in Islam?",
      options: [
        { value: "A", label: "A. To gain profit from charitable investments" },
        { value: "B", label: "B. To ensure the continuous benefit of society through a charitable endowment" },
        { value: "C", label: "C. To transfer wealth to heirs outside of inheritance law" },
        { value: "D", label: "D. To fulfill a personal vow or oath" },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);


  return (
    <div className="container">
       <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={24}
        timerMinutes={60}
        currentLevel={1}
      />
  
     
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 24</b> where we will learn about Waqf, or Islamic
        endowment, which is a charitable act where a person donates an asset
        permanently for the benefit of others while ceasing its sale, transfer,
        or inheritance. This concept is deeply rooted in Islamic teachings and
        is considered a means of ongoing charity (Sadaqah Jariyah). The
        foundation for Waqf is derived from an authentic hadith where Umar ibn
        Al-Khattab (may Allah be pleased with him) acquired land in Khyber and
        sought the Prophet’s (peace be upon him) guidance on how to use it. The
        Prophet advised him: "If you like, you can make it an endowment and give
        its produce in charity without selling it, gifting it, or inheriting
        it."
        <i>
          <a href="https://sunnah.com/bukhari:2737" target="_blank">
            (Sahih al-Bukhari, Hadith 2737)
          </a>
        </i>{" "}
        . Following this guidance, Umar endowed the land so that its benefits
        would continuously serve the needy, demonstrating the essence of Waqf
        and ensuring a lasting impact for the community while keeping the
        principal asset intact.
        <br />
        <br />A fundamental condition of Waqf is that it must be a permanent
        endowment. This means that a person cannot designate as Waqf something
        that does not belong to them in a complete and unrestricted manner. For
        instance, if a person rents land for a limited period, they cannot
        declare it as Waqf, because their ownership is temporary and does not
        grant them the right to make irrevocable commitments. The asset must be
        fully owned and legally recognized as transferable by the donor. This
        principle ensures that Waqf remains a genuine act of devotion, not a
        means of temporarily restricting access to property. The Prophet (peace
        be upon him) established the precedent that Waqf is a perpetual charity
        that must not be undone, underscoring the need for sincerity and
        long-term benefit in such charitable contributions.
        <br />
        <br />
        It is impermissible to endow an item that has no practical use, as this
        would contradict the purpose of Waqf. For example, a person cannot
        dedicate an old, disabled donkey as Waqf because it cannot carry loads
        or serve any productive function, and instead, it would become a
        financial burden requiring upkeep. This aligns with the Islamic
        principle of utility in charity, ensuring that endowments actively serve
        the community. The Prophet (peace be upon him) said that the best
        charity is , "Giving water to drink."{" "}
        <i>
          <a href="https://sunnah.com/ibnmajah:3684" target="blank">
            (Sunan Ibn Majah, Hadith 3684)
          </a>
        </i>
        , emphasizing that charitable acts should fulfill a tangible need. Waqf,
        therefore, is a powerful institution in Islamic finance and social
        welfare, ensuring the long-term sustainability of charitable endeavors
        while maintaining the integrity of the asset for future generations.
        <br />
        <br />
        <b>Clarity and Specificity in Waqf: Defining the Asset for Endowment</b>
        <br />
        <br />
        In Islam, clarity and specificity are essential conditions for the
        validity of a Waqf. A person cannot endow an asset that is vague or
        unknown, as this would lead to uncertainty (gharar), which is prohibited
        in Islamic transactions. For example, if someone were to say, "I donate
        one of my houses as Waqf," without specifying which one, this would be
        impermissible because the asset being endowed is not clearly identified.
        The principle behind this ruling is that Waqf is an irrevocable transfer
        of ownership for the sake of Allah, and any ambiguity in the transaction
        could lead to disputes or mismanagement.
        <br /> However, an exception exists when two assets are completely
        identical in value and characteristics, such as two houses with the same
        market price, size, and features. In such a case, the donor may say, "I
        donate any one of these two houses as Waqf," because either choice
        fulfills the same objective, and there is no essential difference
        between the two. This flexibility ensures that Waqf remains a practical
        and beneficial institution while upholding the principle of specificity.
        <br />
        Another significant aspect of Waqf is that the donor does not need to
        have personal knowledge of the physical details of the asset being
        endowed. If a person owns a house but has never seen it or visited it,
        they may still validly declare it as Waqf. The key factor here is that
        the asset itself is specific and identifiable, even if its precise
        attributes are unknown to the donor. This ruling is based on the fact
        that Waqf is a legal act of devotion rather than a contractual sale,
        meaning that the endowment is not dependent on subjective knowledge but
        rather on the asset's objective existence. By allowing such donations,
        Islamic law ensures that wealth and property continue to serve
        beneficial purposes, even in cases where the owner may lack firsthand
        familiarity with their holdings. This demonstrates the balance in
        Islamic jurisprudence between ensuring legal precision and facilitating
        charitable contributions that benefit society at large.
        <br />
        <br />
        <b>
          Waqf of Consumable Assets: Permissibility and Scholarly Perspectives
        </b>
        <br />
        <br />
        In Islamic jurisprudence, the general principle of Waqf (endowment) is
        that the endowed asset should be preserved while its benefits are
        continuously utilized by the beneficiaries. However, an important
        discussion arises when considering assets that can only be used through
        consumption, such as water and money. While some scholars argue that
        Waqf should be restricted to non-perishable assets like land, buildings,
        or livestock, a stronger and more practical opinion allows for the
        endowment of consumable assets when their use aligns with charitable and
        beneficial purposes. This view is particularly relevant in cases where
        the consumption of the asset directly fulfills an essential need. For
        instance, it is permissible to donate water as Waqf, even though its
        benefit is realized through drinking, which depletes it.
        <br /> This principle extends to the donation of money as Waqf, despite
        the fact that its benefit is derived from spending it. Historically,
        scholars and Islamic institutions have established monetary Waqf by
        using funds to provide ongoing financial assistance, such as lending to
        small businesses or funding education, ensuring that even though the
        money is spent, its impact continues. The permissibility of consumable
        Waqf reflects the flexibility and practicality of Islamic rulings,
        allowing charitable donations to adapt to the needs of the time while
        maintaining the core objective of sustaining communal welfare.
        <br />
        <br />
        <b>
          Endowing an Asset to Oneself: A Means of Preserving Public Benefit
        </b>
        <br />
        <br />
        Many scholars have affirmed the permissibility of a person making a Waqf
        (endowment) to themselves, especially when the intent is to safeguard an
        asset from being sold, gifted, or otherwise removed from benefiting the
        community. This ruling is particularly relevant in cases where an
        individual owns a property or resource that is currently being used for
        charitable or communal purposes, but they fear that future financial
        pressures or personal decisions may lead them to dispose of it. By
        formally endowing the asset to themselves, they effectively relinquish
        their right to sell or transfer ownership, ensuring its perpetual use
        for the benefit of others. <br />
        This practice is particularly beneficial for assets such as wells,
        farmlands, or buildings that serve an ongoing charitable function. Once
        a person establishes a Waqf for themselves, they continue to manage and
        benefit from it within the framework of the endowment’s conditions, but
        they lose the right to dispose of it outside those terms. After the
        individual's death, the asset is typically placed under the
        administration of the official body responsible for managing religious
        endowments in the Muslim community. This principle reflects the
        flexibility of Islamic endowment laws in balancing personal control with
        long-term public welfare, allowing individuals to secure their
        charitable intentions and prevent the loss of assets that provide
        ongoing benefit to society.
        <br />
        <br />
        <b>Endowing an Asset to a Specific Beneficiary</b>
        <br />
        <br />
        In Islamic endowment (Waqf), it is essential that the asset is dedicated
        to a specific person or group that is eligible to possess and benefit
        from it. This ensures that the endowment serves a clear purpose and
        remains under the management of responsible individuals. However, an
        exception to this rule is the mosque, which can be endowed generically
        to the worshippers without singling out any individual, as mosques exist
        for the collective benefit of all Muslims who use them for prayer and
        worship. On the other hand, a Waqf cannot be made in favor of an animal,
        as animals lack the legal capacity to own or manage property. Instead,
        if a person wishes to support animals through Waqf, they must designate
        a responsible human trustee to oversee the care and allocation of
        resources for their well-being.
        <br />
        <br />
        Similarly, a Waqf cannot be established for an unborn child or a
        deceased person. An unborn child does not yet have legal standing and,
        therefore, cannot be entrusted with the management or benefit of an
        endowment. Likewise, a deceased person no longer has the capacity to own
        or manage assets, making it impermissible to dedicate a Waqf in their
        name. Instead, if someone wishes to benefit the deceased, they may
        donate on their behalf, such as building a mosque or establishing a
        charity that benefits the living, as these count as continuous charity
        (Sadaqah Jariyah). These conditions emphasize the structured and
        practical nature of Islamic endowments, ensuring that assets are only
        entrusted to individuals or entities capable of maintaining and
        utilizing them in a manner that aligns with Islamic law.
        <br />
        <br />
        <b>Possessing an Endowed Asset and Beneficiary Acceptance</b>
        <br />
        <br />
        In Islamic law, it is permissible for a person to endow an asset as Waqf
        while still possessing it. There is no obligation for the donor to
        immediately relinquish physical access to the asset, as long as the
        endowment is intended for a charitable purpose and remains dedicated to
        its cause. For example, if a person designates a sum of money as Waqf,
        they may retain it in their possession and distribute it at an
        appropriate time to those who are truly in need. This flexibility
        ensures that the Waqf serves its intended purpose effectively rather
        than being rushed into distribution without due consideration.
        <br />
        Additionally, some scholars hold the opinion that if a Waqf is made in
        favor of a specific individual, the beneficiary must accept it for the
        endowment to take effect. This requirement ensures that the recipient is
        willing and prepared to manage and benefit from the endowed asset. These
        principles reflect the practicality and structured nature of Waqf in
        Islamic jurisprudence, ensuring that both the donor’s intent and the
        recipient’s ability to benefit from the endowment are honored.
        <br />
        <br />
        <b>Management and Distribution of a Waqf</b>
        <br />
        <br />
        If a person does not appoint a specific caretaker for the Waqf, or if
        the appointed caretaker passes away, responsibility for managing the
        endowment falls upon the beneficiaries. If the Waqf was not created for
        a particular person such as a mosque or for a general cause benefiting
        an uncountable group like the poor the ruler assumes responsibility,
        either personally or by delegating someone. <br />
        The caretaker of a Waqf must uphold this duty with integrity and fear of
        Allah, as it is an entrusted responsibility (amanah).
        <br /> When a Waqf is established for one's children, both male and
        female offspring must be treated equally, as they are all included in
        the bequest. Upon the children’s passing, the Waqf typically transitions
        to the sons' children rather than the daughters' children, as lineage
        follows the father in inheritance laws. However, some scholars argue
        that a daughter’s children should also be included since the daughters
        themselves are direct descendants. If a person specifies a Waqf solely
        for his "sons" or the "sons of so-and-so," it applies exclusively to
        males, as indicated in Quranic usage. <br />
        However, when a Waqf is dedicated to a tribe, such as Banu Hashim or
        Banu Tamim, both males and females are included, as tribal names
        encompass all members. If the beneficiaries are too numerous to equally
        share the Waqf’s benefits, it is permissible to allocate it selectively
        among them.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        Today’s lesson focused on the concept of Waqf, which is a unique Islamic
        endowment system in which a person dedicates property or assets
        permanently for the benefit of the community or a charitable cause,
        seeking the pleasure of Allah. We learned that once a property is
        declared as Waqf, it cannot be sold, gifted, or inherited, and its
        benefits must continue to serve the specified purpose. The session
        explored the conditions for establishing a valid Waqf, the types of
        properties that can be endowed, and the roles and responsibilities of
        the mutawalli (trustee). Real-life examples and rulings from the Qur’an
        and Sunnah were discussed to highlight how Waqf plays a critical role in
        supporting education, healthcare, religious institutions, and social
        welfare. The lesson emphasized that Waqf is a long-term form of charity
        (Sadaqah Jariyah), bringing continuous rewards even after one’s death.
        <br />
        <br />
        <b>Coming Up Next</b>
        <br />
        <br />
        As we conclude our discussion on Waqf and its enduring legacy in Islamic
        society, tomorrow we turn to a more personal and direct form of wealth
        transfer i.e Hibah (lifetime grants) and Atiyah (donations made in old
        age or fatal illness). These acts of generosity, often carried out
        within families, raise important legal and ethical questions: What makes
        a grant valid in Shariah? Can a parent take back a gift given to their
        child? And how do Islamic inheritance laws interact with late-life
        donations? On Day 25 we will explore the rules, boundaries, and spirit
        behind these powerful forms of giving.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <p className="content">
        1. What is the primary characteristic of a Waqf property in Islamic law?
        <ul>
          <li>A. It can be sold or inherited</li>
          <li>B. It must generate profit for the founder</li>
          <li>C. It must be permanently dedicated for a charitable purpose</li>
          <li>D. It is returned to the government after 10 years</li>
        </ul>
        2. Who is responsible for managing and maintaining the Waqf property?
        <ul>
          <li>A. The government</li>
          <li>B. The donor’s heirs</li>
          <li>C. The Mutawalli (trustee)</li>
          <li>D. The local imam</li>
        </ul>
        3. Which of the following is a valid example of Waqf in Islamic
        tradition?
        <ul>
          <li>A. Investing money for a business ventures</li>
          <li>B. Renting a book to a friend</li>
          <li>C. Dedicating a piece of land for building a mosque</li>
          <li>D. Giving a loan to a relative without interest</li>
        </ul>
        <ul>
          4. What is the main purpose of establishing a Waqf in Islam?
          <li>A. To gain profit from charitable investments</li>
          <li>
            B. To ensure the continuous benefit of society through a charitable
            endowment
          </li>
          <li>C. To transfer wealth to heirs outside of inheritance law</li>
          <li>D. To fulfill a personal vow or oath</li>
        </ul>
      </p>
    

     <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage24;
